import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export const COOKIE_KEY = "leadders_cookie_consent";

export const getCookieConsent = () => {
  try { return JSON.parse(localStorage.getItem(COOKIE_KEY)); } catch { return null; }
};

const save = (prefs) => {
  localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...prefs, ts: new Date().toISOString(), v: 1 }));
  window.dispatchEvent(new Event("cookie-consent"));
};

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) setOpen(true);
    const reopen = () => { setCustom(true); setOpen(true); };
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, []);

  if (!open) return null;
  const done = (prefs) => { save(prefs); setOpen(false); };

  return (
    <div data-testid="cookie-banner" role="dialog" aria-label="Preferenze cookie"
      className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-5">
      <div className="max-w-3xl mx-auto rounded-2xl border border-white/10 bg-slate-950/95 backdrop-blur-xl text-white shadow-2xl p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <Cookie className="h-6 w-6 text-accent shrink-0 mt-0.5" />
          <div className="flex-1 text-sm leading-relaxed">
            <p className="font-display font-semibold text-base">Usiamo i cookie</p>
            <p className="text-slate-300 mt-1">
              Cookie tecnici necessari al funzionamento del sito e, solo con il tuo consenso, cookie statistici e di marketing.
              Puoi cambiare idea quando vuoi. <Link to="/cookie-policy" className="underline text-accent" data-testid="cookie-policy-link">Cookie Policy</Link>
            </p>
            {custom && (
              <div className="mt-4 space-y-3" data-testid="cookie-custom">
                {[
                  ["Necessari", "Sempre attivi: sessione, sicurezza, preferenze.", true, null],
                  ["Statistici", "Ci aiutano a capire come viene usato il sito.", analytics, setAnalytics],
                  ["Marketing", "Per misurare le campagne pubblicitarie.", marketing, setMarketing],
                ].map(([t, d, v, set]) => (
                  <div key={t} className="flex items-center justify-between gap-4 rounded-lg bg-white/5 px-3 py-2">
                    <div><div className="font-medium">{t}</div><div className="text-xs text-slate-400">{d}</div></div>
                    <Switch checked={v} disabled={!set} onCheckedChange={set || undefined} data-testid={`cookie-switch-${t.toLowerCase()}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-4">
          {!custom && (
            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10" onClick={() => setCustom(true)} data-testid="cookie-customize-button">Personalizza</Button>
          )}
          <Button variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10"
            onClick={() => done({ analytics: false, marketing: false })} data-testid="cookie-reject-button">Rifiuta</Button>
          {custom ? (
            <Button className="bg-accent hover:bg-accent/90 text-white" onClick={() => done({ analytics, marketing })} data-testid="cookie-save-button">Salva preferenze</Button>
          ) : (
            <Button className="bg-accent hover:bg-accent/90 text-white" onClick={() => done({ analytics: true, marketing: true })} data-testid="cookie-accept-button">Accetta tutti</Button>
          )}
        </div>
      </div>
    </div>
  );
}
